export type ConversationEventType = 'transcript' | 'chat' | 'userQuery';

export interface TranscriptTurn {
  id: string;
  eventType: ConversationEventType;
  speaker: string;
  text: string;
  timestamp: string | null;
  startSeconds: number | null;
  endSeconds: number | null;
  dialogueAct: string | null;
  labels: string[];
}

export interface TranscriptDocument {
  meetingId: string | null;
  turns: TranscriptTurn[];
}

const INVALID_JSON_MESSAGE = 'The selected file is not valid JSON.';
const INVALID_TRANSCRIPT_MESSAGE =
  'The selected file does not contain a supported transcript.';
const TEXT_TURN_PATTERN = /^\s*(?:\[([^\]]+)\]\s*)?([^:]+):\s*(.+)\s*$/;
const WEBVTT_HEADER = 'WEBVTT';
const WEBVTT_TIMING_PATTERN =
  /^(\d{2,}:\d{2}:\d{2}\.\d{3}|\d{2}:\d{2}\.\d{3})\s+-->\s+(\S+)/;
const WEBVTT_VOICE_PATTERN = /^<v(?:\.[^ >]+)*(?:\s+([^>]+))?>/i;
const TSV_REQUIRED_HEADERS = [
  'start',
  'end',
  'participant_id',
  'text',
] as const;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const MAX_UNICODE_CODE_POINT = 0x10ffff;
const HEX_RADIX = 16;
const DECIMAL_RADIX = 10;
const HTML_ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}

function optionalSeconds(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is string =>
          typeof item === 'string' && item.trim().length > 0,
      )
    : [];
}

export function decodeConversationText(value: string): string {
  return value.replace(
    /&(#x[0-9a-f]+|#\d+|[a-z]+);/gi,
    (entity, encoded: string) => {
      if (encoded.startsWith('#x') || encoded.startsWith('#X')) {
        const codePoint = Number.parseInt(encoded.slice(2), HEX_RADIX);
        return Number.isInteger(codePoint) &&
          codePoint >= 0 &&
          codePoint <= MAX_UNICODE_CODE_POINT
          ? String.fromCodePoint(codePoint)
          : entity;
      }
      if (encoded.startsWith('#')) {
        const codePoint = Number.parseInt(encoded.slice(1), DECIMAL_RADIX);
        return Number.isInteger(codePoint) &&
          codePoint >= 0 &&
          codePoint <= MAX_UNICODE_CODE_POINT
          ? String.fromCodePoint(codePoint)
          : entity;
      }

      return HTML_ENTITIES[encoded.toLowerCase()] ?? entity;
    },
  );
}

function parseJsonTurn(value: unknown, index: number): TranscriptTurn {
  if (!isRecord(value)) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  const speaker = optionalString(value.speaker);
  const text = optionalString(value.text);
  if (speaker === null || text === null) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  return {
    id: `turn-${index}`,
    eventType: 'transcript',
    speaker,
    text,
    timestamp: null,
    startSeconds: optionalSeconds(value.start),
    endSeconds: optionalSeconds(value.end),
    dialogueAct: optionalString(value.dialogue_act),
    labels: stringArray(value.label),
  };
}

function parseJsonTranscript(source: string): TranscriptDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch {
    throw new Error(INVALID_JSON_MESSAGE);
  }

  if (!isRecord(parsed) || !Array.isArray(parsed.turns)) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  const turns = parsed.turns.map(parseJsonTurn);
  if (turns.length === 0) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  return {
    meetingId: optionalString(parsed.meeting_id),
    turns,
  };
}

function parseTextTranscript(source: string): TranscriptDocument {
  const turns: TranscriptTurn[] = [];

  for (const line of source.split(/\r?\n/)) {
    if (line.trim().length === 0) {
      continue;
    }

    const match = TEXT_TURN_PATTERN.exec(line);
    if (match === null) {
      throw new Error(INVALID_TRANSCRIPT_MESSAGE);
    }

    const dialogueAct = optionalString(match[1]);
    const speaker = match[2]?.trim();
    const text = match[3]?.trim();
    if (!speaker || !text) {
      throw new Error(INVALID_TRANSCRIPT_MESSAGE);
    }

    turns.push({
      id: `turn-${turns.length}`,
      eventType: 'transcript',
      speaker,
      text,
      timestamp: null,
      startSeconds: null,
      endSeconds: null,
      dialogueAct,
      labels: [],
    });
  }

  if (turns.length === 0) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  return { meetingId: null, turns };
}

function parseWebVttTimestamp(value: string): number | null {
  const parts = value.split(':');
  const seconds = Number(parts.at(-1));
  const minutes = Number(parts.at(-2));
  const hours = parts.length === 3 ? Number(parts[0]) : 0;
  if (
    !Number.isFinite(seconds) ||
    !Number.isFinite(minutes) ||
    !Number.isFinite(hours) ||
    seconds < 0 ||
    minutes < 0 ||
    hours < 0
  ) {
    return null;
  }

  return (
    hours * MINUTES_PER_HOUR * SECONDS_PER_MINUTE +
    minutes * SECONDS_PER_MINUTE +
    seconds
  );
}

function parseWebVttTranscript(source: string): TranscriptDocument {
  const blocks = source.split(/\r?\n[ \t]*\r?\n/);
  if (!blocks[0]?.startsWith(WEBVTT_HEADER)) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  const turns: TranscriptTurn[] = [];
  for (const block of blocks.slice(1)) {
    const lines = block.split(/\r?\n/);
    const firstLine = lines[0]?.trim() ?? '';
    if (
      firstLine.startsWith('NOTE') ||
      firstLine === 'STYLE' ||
      firstLine === 'REGION'
    ) {
      continue;
    }

    const timingIndex = lines.findIndex((line) => line.includes('-->'));
    const timingMatch =
      timingIndex === -1
        ? null
        : WEBVTT_TIMING_PATTERN.exec(lines[timingIndex]?.trim() ?? '');
    const cueLines =
      timingIndex === -1 ? [] : lines.slice(timingIndex + 1).filter(Boolean);
    const cueSource = cueLines.join('\n').trim();
    const voiceMatch = WEBVTT_VOICE_PATTERN.exec(cueSource);
    const speaker = voiceMatch?.[1]?.trim();
    const startSeconds =
      timingMatch === null ? null : parseWebVttTimestamp(timingMatch[1] ?? '');
    const endSeconds =
      timingMatch === null ? null : parseWebVttTimestamp(timingMatch[2] ?? '');
    const text = decodeConversationText(
      cueSource.replace(/<[^>]*>/g, '').trim(),
    );
    if (
      timingMatch === null ||
      startSeconds === null ||
      endSeconds === null ||
      endSeconds < startSeconds ||
      !speaker ||
      text.length === 0
    ) {
      throw new Error(INVALID_TRANSCRIPT_MESSAGE);
    }

    turns.push({
      id: `turn-${turns.length}`,
      eventType: 'transcript',
      speaker,
      text,
      timestamp: null,
      startSeconds,
      endSeconds,
      dialogueAct: null,
      labels: [],
    });
  }

  if (turns.length === 0) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  return { meetingId: null, turns };
}

function requiredTsvColumn(headers: readonly string[], name: string): number {
  const index = headers.indexOf(name);
  if (index === -1) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }
  return index;
}

function parseTsvSeconds(value: string | undefined): number | null {
  if (value === undefined || value.trim() === '') {
    return null;
  }
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

function parseTsvTranscript(source: string): TranscriptDocument {
  const [headerLine, ...rows] = source.split(/\r?\n/);
  const headers =
    headerLine?.split('\t').map((header) => header.trim().toLowerCase()) ?? [];
  for (const requiredHeader of TSV_REQUIRED_HEADERS) {
    requiredTsvColumn(headers, requiredHeader);
  }
  const startIndex = requiredTsvColumn(headers, 'start');
  const endIndex = requiredTsvColumn(headers, 'end');
  const speakerIndex = requiredTsvColumn(headers, 'participant_id');
  const textIndex = requiredTsvColumn(headers, 'text');
  const turns: TranscriptTurn[] = [];

  for (const row of rows) {
    if (row.trim() === '') {
      continue;
    }
    const values = row.split('\t');
    const startSeconds = parseTsvSeconds(values[startIndex]);
    const endSeconds = parseTsvSeconds(values[endIndex]);
    const speaker = values[speakerIndex]?.trim();
    const text = decodeConversationText(
      values.slice(textIndex).join('\t').trim(),
    );
    if (
      startSeconds === null ||
      endSeconds === null ||
      endSeconds < startSeconds ||
      !speaker ||
      text.length === 0
    ) {
      throw new Error(INVALID_TRANSCRIPT_MESSAGE);
    }

    turns.push({
      id: `turn-${turns.length}`,
      eventType: 'transcript',
      speaker,
      text,
      timestamp: null,
      startSeconds,
      endSeconds,
      dialogueAct: null,
      labels: [],
    });
  }

  if (turns.length === 0) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  return { meetingId: null, turns };
}

export function parseTranscript(
  source: string,
  fileName = '',
): TranscriptDocument {
  const trimmedSource = source.trim();
  const normalizedFileName = fileName.trim().toLowerCase();
  if (
    normalizedFileName.endsWith('.vtt') ||
    trimmedSource.startsWith(WEBVTT_HEADER)
  ) {
    return parseWebVttTranscript(trimmedSource);
  }
  if (normalizedFileName.endsWith('.tsv')) {
    return parseTsvTranscript(trimmedSource);
  }
  const isJson =
    normalizedFileName.endsWith('.json') ||
    (!normalizedFileName.endsWith('.txt') && trimmedSource.startsWith('{'));

  return isJson
    ? parseJsonTranscript(trimmedSource)
    : parseTextTranscript(trimmedSource);
}
